import { createApi } from '@reduxjs/toolkit/query/react'
import baseQueryWithReauth from './baseQuery'
import { apiTags } from './apiTags'

export interface Chat {
  id: string
  name: string
  description?: string
  created_at: string
  updated_at: string
}

export interface ChatMessage {
  ID: string
  UserID: string
  ChatID: string
  RequestID: string
  Type: 'query' | 'response'
  Prompt: string
  Response: string
  GeneratedTags: any | null
  Domain: string
  ProjectID: string
  DatasetID: string
  PendingTool: any | null
  ToolResult: any | null
  CreatedAt: string
  ExpiresAt: string
  metadata_filter?: {
    datasource: string[]
  }
  model_id?: string
}

export interface CreateChatRequest {
  name: string
  description?: string
}

export interface UpdateChatRequest {
  name?: string
  description?: string
}

export interface SendMessageRequest {
  chat_id: string
  message: string
  model_type: 'qwen2.5:14b'
  project_id?: string
  dataset_id?: string
  metadata_filter?: {
    datasource: string[]
  }
  model_id?: string
}

export interface TransformedChatMessage extends ChatMessage {
  role: 'user' | 'assistant'
  content: string
  id: string
}

export interface ChatHistoryResponse {
  messages: TransformedChatMessage[]
}

export interface ListChatsResponse {
  chats: Chat[]
}

// Define API endpoints as constants
const CHATS_URL = 'experimental/chats'

export const chatApi = createApi({
  reducerPath: 'chatApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [apiTags.Chat, apiTags.ChatList, apiTags.ChatMessage],

  endpoints: (builder) => ({
    listChats: builder.query<ListChatsResponse, void>({
      query: () => ({
        url: CHATS_URL,
        method: 'GET',
      }),
      providesTags: (result) => [
        { type: apiTags.ChatList, id: 'LIST' },
        ...(result?.chats?.map(({ id }) => ({ type: apiTags.Chat, id })) || []),
      ],
    }),

    createChat: builder.mutation<Chat, CreateChatRequest>({
      query: (chatData) => ({
        url: CHATS_URL,
        method: 'POST',
        body: chatData,
      }),
      invalidatesTags: (result) => [
        { type: apiTags.ChatList, id: 'LIST' },
        ...(result?.id
          ? [
              { type: apiTags.Chat, id: result.id },
              { type: apiTags.ChatMessage, id: result.id },
            ]
          : []),
      ],
    }),

    updateChat: builder.mutation<Chat, { chatId: string; chatData: UpdateChatRequest }>({
      query: ({ chatId, chatData }) => ({
        url: `${CHATS_URL}/${chatId}`,
        method: 'PUT',
        body: chatData,
      }),
      invalidatesTags: (result, error, { chatId }) => [
        { type: apiTags.Chat, id: chatId },
        { type: apiTags.ChatList, id: 'LIST' },
      ],
    }),

    deleteChat: builder.mutation<void, string>({
      query: (chatId) => ({
        url: `${CHATS_URL}/${chatId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, chatId) => [
        { type: apiTags.Chat, id: chatId },
        { type: apiTags.ChatList, id: 'LIST' },
        { type: apiTags.ChatMessage, id: chatId },
      ],
    }),

    getChatHistory: builder.query<ChatHistoryResponse, string>({
      query: (chatId) => ({
        url: `${CHATS_URL}/${chatId}/history`,
        method: 'GET',
      }),
      transformResponse: (response: ChatMessage[]): ChatHistoryResponse => ({
        messages:
          response?.length > 0
            ? response
                .sort((a, b) => new Date(a.CreatedAt).getTime() - new Date(b.CreatedAt).getTime())
                .flatMap((msg) => [
                  {
                    ...msg,
                    role: 'user' as const,
                    content: msg.Prompt,
                    id: `${msg.ID}-user`,
                  },
                  {
                    ...msg,
                    role: 'assistant' as const,
                    content: msg.Response,
                    id: `${msg.ID}-assistant`,
                  },
                ])
            : [],
      }),
      providesTags: (result, error, chatId) => [
        { type: apiTags.Chat, id: chatId },
        { type: apiTags.ChatMessage, id: chatId },
      ],
      keepUnusedDataFor: 0,
    }),

    sendMessage: builder.mutation<ChatMessage, SendMessageRequest>({
      query: ({ chat_id, ...messageData }) => ({
        url: `${CHATS_URL}/${chat_id}/messages`,
        method: 'POST',
        body: {
          ...messageData,
          model_type: 'qwen2.5:14b',
          metadata_filter: messageData.metadata_filter,
          model_id: messageData.model_id,
        },
      }),
      invalidatesTags: (result, error, { chat_id }) => [
        { type: apiTags.ChatMessage, id: chat_id },
        { type: apiTags.Chat, id: chat_id },
        { type: apiTags.ChatList, id: 'LIST' },
      ],
    }),
  }),
})

export const {
  useListChatsQuery,
  useCreateChatMutation,
  useUpdateChatMutation,
  useDeleteChatMutation,
  useGetChatHistoryQuery,
  useSendMessageMutation,
} = chatApi
